import { Form, Input, Button, Divider, Checkbox, Typography } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import useLoading from '../../../../hooks/useLoading';

export type Props = {
  onSubmit: (email: string, password: string) => void;
  buttonText: string;
  failedCallback?: (info: any) => void;
  showConfirm?: boolean;
};
export const SignInForm = (props: Props) => {
  const {
    onSubmit,
    buttonText,
    failedCallback = () => {},
    showConfirm = false,
  } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { isLoading, startLoading, stopLoading } = useLoading();

  const onFinish = async (values: any) => {
    startLoading();
    await onSubmit(values.email, values.password);
    stopLoading();
  };

  const onFinishFailed = (errorInfo: any) => {
    failedCallback(errorInfo);
  };

  return (
    <Form
      name="loginForm"
      form={form}
      initialValues={{ remember: true }}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      autoComplete="off"
      style={{ width: '100%' }}
      layout="vertical" // フォームのレイアウトを垂直に設定
    >
      <Form.Item
        label={t('auth.email')}
        name="email"
        rules={[
          { required: true, message: t('common.required') },
          { type: 'email', message: t('auth.invalidEmail') },
        ]}
      >
        <Input size="large" prefix={<MailOutlined />} />
      </Form.Item>

      <Form.Item
        label={t('auth.password')}
        name="password"
        rules={[
          { required: true, message: t('common.required') },
          { min: 6, message: t('auth.passwordMin') },
        ]}
        hasFeedback
      >
        <Input.Password size="large" prefix={<LockOutlined />} />
      </Form.Item>
      {showConfirm && (
        <Form.Item
          label={t('auth.passwordConfirm')}
          name="passwordConfirm"
          rules={[
            { required: true, message: t('common.required') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('auth.passwordMismatch')));
              },
            }),
          ]}
          hasFeedback
        >
          <Input.Password size="large" prefix={<LockOutlined />} />
        </Form.Item>
      )}
      {showConfirm && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center', // Flexアイテムを横軸（主軸ではない）中央に配置
            padding: 10,
            backgroundColor: '#F5FAFF',
            borderRadius: 5,
            textAlign: 'center', // テキストを中央寄せ
          }}
        >
          <Typography.Text style={{ fontSize: 14 }}>
            {t('auth.agreeLead')}
          </Typography.Text>
          <Typography.Text
            style={{ fontSize: 10, paddingTop: 4, textAlign: 'start' }}
          >
            {t('auth.privacyNotice1')} {t('auth.privacyNotice2')}
          </Typography.Text>
          <Typography.Text style={{ fontSize: 10, textAlign: 'start' }}>
            {t('auth.privacyNotice3')}
          </Typography.Text>
          <div
            style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}
          >
            <a
              href="https://www.ohat.jp/%E5%88%A9%E7%94%A8%E8%A6%8F%E7%B4%84"
              target="_blank"
              rel="noreferrer"
            >
              <Typography.Text
                style={{
                  paddingTop: 4,
                  fontSize: 12,
                  textAlign: 'center',
                  textDecorationLine: 'underline',
                }}
              >
                {t('footer.terms')}
              </Typography.Text>
            </a>
            <a
              href="https://medi-banx.com/privacy/"
              target="_blank"
              rel="noreferrer"
            >
              <Typography.Text
                style={{
                  paddingTop: 4,
                  fontSize: 12,
                  textAlign: 'center',
                  textDecorationLine: 'underline',
                }}
              >
                {t('footer.privacy')}
              </Typography.Text>
            </a>
          </div>
          <div style={{ paddingTop: 4 }}>
            <Form.Item
              valuePropName="checked"
              name="agreement"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('auth.agreeRequired'))),
                },
              ]}
            >
              <Checkbox>{t('auth.agree')}</Checkbox>
            </Form.Item>
          </div>
        </div>
      )}
      <Divider />
      <Form.Item>
        <Button
          size="large"
          type="primary"
          block
          htmlType="submit"
          loading={isLoading}
        >
          {buttonText}
        </Button>
      </Form.Item>
    </Form>
  );
};
